import queryParser from "../../utils/queryParser";
import { prepChat } from "../../encoding";
import { getConfig } from "../../webcoa/config";
import { capLogNodes } from "../../client/logHistory";
import { showLightbox } from "../../client/oocLightbox";
import { oocImagesEnabled } from "../../client/oocImages";
const { mode } = queryParser();

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|avif)$/i;
const URL_RE = /(https?:\/\/[^\s]+)/g;

/**
 * Parses a URL that is eligible to be an embedded image by POLICY
 * (allowed protocol + image-looking path), independent of whether images
 * are currently toggled on. The protocol allow-list also rejects
 * javascript:/data:, so there is no injection vector -- and because we
 * build DOM nodes (never innerHTML) a hostile "URL" can at worst be a
 * broken <img>/<a>, never script.
 */
function parseImageUrl(raw: string): URL | null {
  let cfg;
  try {
    cfg = getConfig();
  } catch {
    return null;
  }
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (!cfg.oocImages.allowedProtocols.includes(u.protocol)) return null;
  // Strip query/fragment when testing the extension (CDN URLs append ?ex=).
  if (!IMAGE_EXT.test(u.pathname)) return null;
  return u;
}

/** Builds an inline thumbnail that expands to a lightbox on click.
 *  `onSettled` fires once the image loads or fails, so the caller can
 *  re-run autoscroll (a freshly-loaded image changes the log height). */
function makeImageNode(u: URL, onSettled: () => void): HTMLElement {
  const img = document.createElement("img");
  img.className = "ooc_image";
  img.src = u.href;
  img.loading = "lazy";
  img.alt = "embedded image";
  img.title = "Click to expand";
  img.addEventListener("click", () => showLightbox(u.href));
  img.addEventListener("load", onSettled);
  // If it fails to load, degrade to a plain link rather than a broken icon.
  img.addEventListener("error", () => {
    const a = makeLinkNode(u.href);
    img.replaceWith(a);
    onSettled();
  });
  return img;
}

function makeLinkNode(href: string, isImageUrl = false): HTMLAnchorElement {
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.textContent = href;
  // Tag links that point at images so the OOC-images toggle can convert
  // them back into thumbnails later.
  if (isImageUrl) a.dataset.oocImageLink = "1";
  return a;
}

/**
 * Renders an OOC message body into DOM nodes: plain text stays text
 * (newlines and spacing preserved by the .ooc_line `white-space:
 * pre-wrap` rule), image URLs become inline thumbnails when images are
 * enabled (or tagged links when disabled), other URLs become links.
 * Built entirely with text/elements so user content is never parsed as
 * HTML.
 */
function renderOocBody(text: string, onImageSettled: () => void): DocumentFragment {
  const frag = document.createDocumentFragment();
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
    }
    const raw = m[0];
    const img = parseImageUrl(raw);
    if (img && oocImagesEnabled()) {
      frag.appendChild(document.createElement("br"));
      frag.appendChild(makeImageNode(img, onImageSettled));
    } else if (img) {
      // Eligible image URL but images are toggled off: tagged link.
      frag.appendChild(makeLinkNode(raw, true));
    } else {
      frag.appendChild(makeLinkNode(raw));
    }
    lastIndex = m.index + raw.length;
  }
  if (lastIndex < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  return frag;
}

/**
 * Handles an out-of-character chat message.
 * @param {Array} args packet arguments
 */
export const handleCT = (args: string[]) => {
  if (mode === "replay") return;
  const oocLog = document.getElementById("client_ooclog")!;

  // Capture whether the user is pinned to the bottom BEFORE we append, so
  // we can keep them there even as late-loading images grow the log.
  const wasAtBottom =
    oocLog.scrollTop + oocLog.offsetHeight + 120 > oocLog.scrollHeight;
  const scrollIfWanted = () => {
    if (wasAtBottom) oocLog.scrollTo(0, oocLog.scrollHeight);
  };

  const line = document.createElement("div");
  line.className = "ooc_line";

  const nameSpan = document.createElement("span");
  nameSpan.className = "ooc_name";
  nameSpan.textContent = prepChat(args[1]) + ": ";
  line.appendChild(nameSpan);

  // prepChat neutralizes < > (fullwidth) and unescapes AO codes; the
  // result is plain text we then segment into text/link/image nodes.
  // Newlines are preserved by CSS (white-space: pre-wrap on .ooc_line),
  // so multi-line server output like /getareas keeps its layout.
  const body = prepChat(args[2]).replace(/\r/g, "");
  line.appendChild(renderOocBody(body, scrollIfWanted));

  oocLog.appendChild(line);
  scrollIfWanted();
  capLogNodes(oocLog);
};
